/// Feedback modal — opened from the toolbar Feedback button on
/// both the home and game screens. Five-star rating + optional
/// text comment. Submits to the `submitFeedbackPost` Cloud
/// Function which publishes the feedback as an anonymous post on
/// the Talk-to-the-developer board (public, no identifier) and
/// stores the submitter UID in a private mapping for reply
/// routing. The player reads "Thanks!" + auto-dismiss; failures
/// stay open with an inline error so the player can retry / see
/// what's wrong.

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { submitFeedback } from '../data/feedback'

const TEXT_MAX = 2000

export function FeedbackModal(props: {
  /// Optional level id stamped onto the doc. Game-screen invocation
  /// passes the active level; home-screen invocation passes null.
  levelId?: string | null
  onDismiss: () => void
}) {
  const [rating, setRating] = useState<number | null>(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const handleSubmit = async () => {
    if (rating === null) {
      setError('Please pick a rating.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await submitFeedback({ rating, text, levelId: props.levelId ?? null })
      setDone(true)
      window.setTimeout(props.onDismiss, 1400)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setError(message)
      setBusy(false)
    }
  }

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-50 overflow-y-auto bg-black/70 px-4 py-6 backdrop-blur-sm"
      onClick={busy ? undefined : props.onDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto w-full max-w-sm rounded-xl bg-slate-900 p-6 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">
          Feedback
        </div>
        <h2 className="mt-1 text-lg font-semibold tracking-wide">
          {done ? 'Thanks!' : 'How are we doing?'}
        </h2>

        {!done && (
          <>
            <p className="mt-2 text-xs text-slate-400">
              A rating and a few words of feedback go a long way. We read
              every one.
            </p>

            <div className="mt-4 flex justify-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  disabled={busy}
                  aria-label={`${n} star${n === 1 ? '' : 's'}`}
                  className={
                    'text-3xl transition-colors ' +
                    (rating !== null && n <= rating
                      ? 'text-amber-400'
                      : 'text-slate-600 hover:text-slate-400')
                  }
                >
                  ★
                </button>
              ))}
            </div>

            <label className="mt-4 block">
              <span className="text-[11px] font-semibold text-slate-400">
                Comment <span className="text-slate-500">(optional)</span>
              </span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, TEXT_MAX))}
                disabled={busy}
                rows={4}
                placeholder="What worked? What didn't? What would you change?"
                className="mt-1 w-full resize-none rounded-md bg-slate-800 px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-emerald-500"
              />
              <span className="mt-1 block text-right text-[10px] text-slate-500">
                {text.length} / {TEXT_MAX}
              </span>
            </label>

            <p className="mt-3 text-[10px] leading-relaxed text-slate-500">
              Your feedback is published anonymously on{' '}
              <span className="text-slate-400">Talk to the developer</span> — no name attached. We keep your account ID privately so we can notify you when the developer replies. Submitting counts as consent; you can request deletion any time via Contact Support.
            </p>

            {error && (
              <div className="mt-2 rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">
                {error}
              </div>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={props.onDismiss}
                disabled={busy}
                className="text-xs text-slate-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={busy}
                className={
                  'rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow ' +
                  (busy ? 'opacity-60' : 'hover:bg-emerald-500')
                }
              >
                {busy ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}

        {done && (
          <p className="mt-3 text-sm text-slate-300">
            Posted anonymously on Talk to the developer. We'll notify you here if the developer replies.
          </p>
        )}
      </div>
    </div>,
    document.body,
  )
}
